/**
 * `growth/gate` — ad eligibility. **PURE** function (no SDK, no clock,
 * no storage): takes the state and returns `{ allow, reason }`.
 *
 * Implements the COMPLETE frequency capping table of the ads strategy:
 *
 * | Regla                                   | Default |
 * |-----------------------------------------|---------|
 * | Interstitial per session                 | 1, and only with ≥1 value unit |
 * | Interstitial per day                     | 3 |
 * | Minimum gap between interstitials        | ≥10 min |
 * | First interstitial ever                  | never in the 1st session |
 * | Native per screen                       | 1 |
 * | Banner visible                          | 1 |
 * | Rewarded                                | 3 rewards/day PER TYPE |
 * | Ignored rewarded offer                   | 2/day, with backoff to 1 after 3 days |
 * | Global interruption budget               | ≤1 interruptive / 10 min active |
 * | Premium                                 | 0 ads, inmediato |
 *
 * The `reason` is a stable code (not UI text): it feeds telemetry and
 * is what the tests assert.
 *
 * The DAILY counters already arrive normalized by `AdCapsStore` (which resets
 * on day change): the gate deliberately knows no time zones.
 */

import type { AdCapPolicy, AdGateDecision, AdGateInput, AdGateReason, AdKind } from "./types";

const MINUTE_MS = 60_000;

/** Portfolio defaults (§4 of the strategy). Config-over-code: each app
 *  can override them per placement without touching this function. */
export const DEFAULT_AD_CAP_POLICY: AdCapPolicy = {
  interstitialPerSession: 1,
  interstitialPerDay: 3,
  minGapBetweenInterstitialsMs: 10 * MINUTE_MS,
  allowInterstitialOnFirstSession: false,
  minUnitsCompleted: 1,
  rewardsPerDayPerType: 3,
  offersPerDayPerPlacement: 2,
  ignoredDaysBeforeBackoff: 3,
  offersPerDayWhenBackedOff: 1,
  nativePerScreen: 1,
  bannersVisible: 1,
  interruptionBudgetMs: 10 * MINUTE_MS,
};

/**
 * Children regime (COPPA / Play Families / App Store Kids): STRICTER than
 * the general one. The interstitial only lives `between-screens` (never inside a
 * game), max 1/session and 2/day; native is not used; rewarded requires an adult
 * gate and is off by default.
 */
export const CHILD_AD_CAP_POLICY: AdCapPolicy = {
  ...DEFAULT_AD_CAP_POLICY,
  interstitialPerSession: 1,
  interstitialPerDay: 2,
  rewardsPerDayPerType: 1,
};

/**
 * Field-by-field hardening. `min` for the CAPS (the fewer ads the better)
 * and `max` for the GAPS and thresholds (the longer between interruptions, and
 * the more value units required, the better).
 *
 * `undefined` = the layer does not declare that field ⇒ it contributes nothing
 * and, above all, **it does not loosen what it stays silent about**.
 */
function tighterMin(current: number, candidate: number | undefined): number {
  return candidate === undefined ? current : Math.min(current, candidate);
}

function tighterMax(current: number, candidate: number | undefined): number {
  return candidate === undefined ? current : Math.max(current, candidate);
}

/** In permissive flags, `false` is always the strict setting. */
function tighterFlag(current: boolean, candidate: boolean | undefined): boolean {
  return candidate === undefined ? current : current && candidate;
}

/**
 * Merges capping policies ALWAYS taking the strictest value of each field.
 *
 * ## Why a spread is not enough
 *
 * `{ ...base, ...override }` lets the LAST one win, and that turns any
 * later layer —a tier regime, a back-office configuration DTO—
 * into a way to **LOOSEN** the strictest cap. It is the third hole of this
 * family in the portfolio (the other two were closed by `clampAdsConfigToTier` and
 * `hardenAdInitOptions`), and it shows up exactly when the config is wired into the gate:
 * an `interstitialPerDay: 3` arriving as an override loosens a regime that
 * said 1. The config, which should only be able to tighten, served to loosen.
 *
 * The rule this turns into code:
 *
 * > **A configuration layer can only HARDEN the behaviour; it must never
 * > loosen it below the minimum the policy imposes.**
 *
 * ## Coverage is demanded by the COMPILER
 *
 * The object is built whole, field by field: adding a key to
 * [`AdCapPolicy`] without deciding its direction here **does not compile**. That is the
 * difference with a `Record<keyof …, "min" | "max">` table walked at runtime, which can
 * silently come up short.
 */
export function strictestAdCapPolicy(
  base: AdCapPolicy,
  ...overrides: (Partial<AdCapPolicy> | undefined)[]
): AdCapPolicy {
  let out: AdCapPolicy = { ...base };
  for (const o of overrides) {
    if (!o) {
      continue;
    }
    out = {
      interstitialPerSession: tighterMin(out.interstitialPerSession, o.interstitialPerSession),
      interstitialPerDay: tighterMin(out.interstitialPerDay, o.interstitialPerDay),
      minGapBetweenInterstitialsMs: tighterMax(
        out.minGapBetweenInterstitialsMs,
        o.minGapBetweenInterstitialsMs,
      ),
      allowInterstitialOnFirstSession: tighterFlag(
        out.allowInterstitialOnFirstSession,
        o.allowInterstitialOnFirstSession,
      ),
      minUnitsCompleted: tighterMax(out.minUnitsCompleted, o.minUnitsCompleted),
      rewardsPerDayPerType: tighterMin(out.rewardsPerDayPerType, o.rewardsPerDayPerType),
      offersPerDayPerPlacement: tighterMin(
        out.offersPerDayPerPlacement,
        o.offersPerDayPerPlacement,
      ),
      ignoredDaysBeforeBackoff: tighterMin(
        out.ignoredDaysBeforeBackoff,
        o.ignoredDaysBeforeBackoff,
      ),
      offersPerDayWhenBackedOff: tighterMin(
        out.offersPerDayWhenBackedOff,
        o.offersPerDayWhenBackedOff,
      ),
      nativePerScreen: tighterMin(out.nativePerScreen, o.nativePerScreen),
      bannersVisible: tighterMin(out.bannersVisible, o.bannersVisible),
      interruptionBudgetMs: tighterMax(out.interruptionBudgetMs, o.interruptionBudgetMs),
    };
  }
  return out;
}

/** Formats allowed in the children regime (§6). */
const CHILD_ALLOWED_KINDS: ReadonlySet<AdKind> = new Set(["banner", "interstitial", "rewarded"]);

function deny(reason: AdGateReason): AdGateDecision {
  return { allow: false, reason };
}

const ALLOW: AdGateDecision = { allow: true, reason: "allow" };

function interstitialDecision(i: AdGateInput, p: AdCapPolicy): AdGateDecision {
  const { caps, session } = i;

  // The first run is fully honoured: native/banner may live on day 1, the
  // interstitial may not. Burning the first session kills retention (and LTV).
  if (session.isFirstEver && !p.allowInterstitialOnFirstSession) {
    return deny("first-session");
  }
  // An interstitial is only legal AFTER completing a value unit: that is what
  // turns "screen done" into a breather and not into an interruption.
  if (session.unitsCompleted < p.minUnitsCompleted) {
    return deny("no-value-unit");
  }
  if (caps.interstitialsThisSession >= p.interstitialPerSession) {
    return deny("session-cap");
  }
  if (caps.interstitialsToday >= p.interstitialPerDay) {
    return deny("daily-cap");
  }
  if (
    caps.lastInterstitialAt !== undefined &&
    i.now - caps.lastInterstitialAt < p.minGapBetweenInterstitialsMs
  ) {
    return deny("min-gap");
  }
  // Global budget: ≤1 interruptive impression per `interruptionBudgetMs`
  // of ACTIVE use. The session's first one is free (0 × budget ≤ activeMs);
  // from then on every new interruption has to be "earned" with real use.
  if (caps.interstitialsThisSession * p.interruptionBudgetMs > session.activeMs) {
    return deny("interruption-budget");
  }
  return ALLOW;
}

function rewardedDecision(i: AdGateInput, p: AdCapPolicy): AdGateDecision {
  const { caps, placementId, rewardType } = i;

  // Anti-farming: the cap is per reward TYPE, not per placement — otherwise
  // two placements that give the same thing would double the ceiling.
  const type = rewardType ?? placementId;
  if ((caps.rewardsToday[type] ?? 0) >= p.rewardsPerDayPerType) {
    return deny("reward-daily-cap");
  }

  // Backoff: if the user has been ignoring the offer for days, it is shown less.
  const ignored = caps.ignoredDays[placementId] ?? 0;
  const offerLimit =
    ignored >= p.ignoredDaysBeforeBackoff
      ? p.offersPerDayWhenBackedOff
      : p.offersPerDayPerPlacement;
  const offers = caps.offersToday[placementId] ?? 0;
  if (offers >= offerLimit) {
    return deny(ignored >= p.ignoredDaysBeforeBackoff ? "offer-backoff" : "offer-daily-cap");
  }
  return ALLOW;
}

/**
 * Should this ad be shown NOW?
 *
 * Order of the denials on purpose: first the absolute ones (premium, task in
 * progress, children) and then the capping. That way the `reason` reaching telemetry
 * names the REAL cause and not "cap reached" when it was actually premium.
 */
export function adGate(input: AdGateInput): AdGateDecision {
  // Two bases, two DISTINCT semantics on purpose:
  //
  //  * `DEFAULT_AD_CAP_POLICY` is a **portfolio default**: each app defines
  //    its regime on top, and it can differ in both directions (a B tier
  //    legitimately allows more than one interstitial per session — in fact the
  //    interruption budget is ONLY reachable in that case).
  //  * `CHILD_AD_CAP_POLICY` es un **suelo legal** (COPPA / Play Families /
  //    Kids). There is no negotiation there: the app's policy can only TIGHTEN.
  //    A children regime that can be loosened from a layer above is not
  //    a children regime.
  //
  // The other half of the rule —"the CONFIGURATION only hardens"— cannot be
  //  resolved here: when the caller has already merged its regime with the config
  //  through a spread, the strictness was lost BEFORE this function and there is
  //  nothing to rebuild. That is why each app composes its policy through a single
  //  door with `strictestAdCapPolicy` (in bso, `bsoAdCapPolicy`).
  const policy: AdCapPolicy = input.user.child
    ? strictestAdCapPolicy(CHILD_AD_CAP_POLICY, input.policy)
    : { ...DEFAULT_AD_CAP_POLICY, ...input.policy };

  // 1. Premium removes the ads across the WHOLE app, immediately and without
  //    waiting for the sync (the optimistic local flag wins after the purchase).
  if (input.user.premium) {
    return deny("premium");
  }

  // 2. Never in the middle of a task: active input, running timer, half-done
  //    half-done or gesture in progress. It wins over any capping schedule.
  if (input.inFlight) {
    return deny("in-flight");
  }

  // 3. Children regime.
  if (input.user.child) {
    if (!CHILD_ALLOWED_KINDS.has(input.kind)) {
      return deny("child-policy");
    }
    if (input.kind === "interstitial" && input.breakpoint !== "between-screens") {
      // Never inside the game: only the transition between screens.
      return deny("child-policy");
    }
    if (input.kind === "rewarded" && !input.user.adultGateCleared) {
      // Rewarded disabled by default: only after the parental gate.
      return deny("child-policy");
    }
  }

  switch (input.kind) {
    case "interstitial": {
      return interstitialDecision(input, policy);
    }
    case "rewarded": {
      return rewardedDecision(input, policy);
    }
    case "native": {
      return input.caps.nativesOnScreen >= policy.nativePerScreen ? deny("screen-cap") : ALLOW;
    }
    case "banner": {
      return input.caps.bannersVisible >= policy.bannersVisible ? deny("screen-cap") : ALLOW;
    }
    default: {
      return deny("child-policy");
    }
  }
}
